<script setup lang="ts">
import { AccountIcon, EmptyState, MenuItem, MenuPopover } from '@taler/ui'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import type { LargestTransactionRow } from '../internal/view-types.ts'
import { useI18n } from 'vue-i18n'

defineProps<{ rows: readonly LargestTransactionRow[]; title: string }>()
const { t } = useI18n()

const CELL = 'px-[var(--space-group)] py-[var(--space-inline)]'
</script>

<template>
  <section
    class="flex min-w-0 flex-col overflow-hidden rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
  >
    <header
      class="flex items-center justify-between gap-2 px-[var(--space-group)] pt-[var(--space-group)] pb-[var(--space-inline)]"
    >
      <h2 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
        {{ title }}
      </h2>
    </header>
    <EmptyState v-if="rows.length === 0" :title="t('reporting.largest.empty')" />
    <div v-else class="overflow-x-auto">
      <table class="w-full border-collapse text-left text-[length:var(--text-step-2)]">
        <caption class="sr-only">
          {{
            title
          }}
        </caption>
        <thead class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          <tr class="border-y border-[color:var(--color-border)]">
            <th scope="col" :class="[CELL, 'font-normal']">{{ t('reporting.largest.date') }}</th>
            <th scope="col" :class="[CELL, 'font-normal']">{{ t('reporting.largest.payee') }}</th>
            <th scope="col" :class="[CELL, 'font-normal']">{{ t('reporting.largest.category') }}</th>
            <th scope="col" :class="[CELL, 'font-normal']">{{ t('reporting.largest.account') }}</th>
            <th scope="col" :class="[CELL, 'text-right font-normal']">{{ t('reporting.largest.amount') }}</th>
            <th scope="col" :class="[CELL, 'font-normal']">{{ t('reporting.largest.tags') }}</th>
            <th scope="col" class="w-px px-[var(--space-group)] py-[var(--space-inline)]">
              <span class="sr-only">{{ t('reporting.largest.actions') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.key"
            class="border-b border-[color:var(--color-border)] last:border-b-0"
          >
            <td class="whitespace-nowrap tabular-nums text-[color:var(--color-text-muted)]" :class="CELL">
              {{ row.date }}
            </td>
            <td class="font-medium text-[color:var(--color-text)]" :class="CELL">{{ row.payee }}</td>
            <td :class="CELL">
              <span class="flex min-w-0 items-center gap-2 text-[color:var(--color-text)]">
                <span aria-hidden="true" class="shrink-0"
                  ><AccountIcon v-if="row.categoryIcon" :icon="row.categoryIcon" label=""
                /></span>
                <span class="truncate">{{ row.category }}</span>
              </span>
            </td>
            <td class="text-[color:var(--color-text-muted)]" :class="CELL">{{ row.account }}</td>
            <td class="text-right" :class="CELL">
              <MoneyAmount
                :value="row.amount"
                sign
                :tone="row.amount.units < 0n ? 'expense' : 'income'"
                size="figure"
              />
            </td>
            <td :class="CELL">
              <ul class="flex flex-wrap gap-1">
                <li
                  v-for="tag in row.tags"
                  :key="tag"
                  class="rounded-full bg-[color:var(--color-surface-sunken)] px-2 py-px text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
                >
                  {{ tag }}
                </li>
              </ul>
            </td>
            <td class="w-px px-[var(--space-group)] py-[var(--space-inline)]">
              <MenuPopover side="bottom" align="end">
                <template #trigger>
                  <button
                    type="button"
                    :aria-label="t('transactions.rowActions', { entry: row.payee })"
                    class="flex h-[var(--density-control)] w-[var(--density-control)] items-center justify-center rounded-[var(--radius-control)] text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface-raised)] hover:text-[color:var(--color-text)]"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
                      <circle cx="3.5" cy="8" r="1.4" />
                      <circle cx="8" cy="8" r="1.4" />
                      <circle cx="12.5" cy="8" r="1.4" />
                    </svg>
                  </button>
                </template>
                <MenuItem as-child>
                  <RouterLink :to="{ name: 'transactions', query: { entry: row.entryId } }">
                    {{ t('transactions.openAction') }}
                  </RouterLink>
                </MenuItem>
              </MenuPopover>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
